import { PolicyLayout, PolicySection } from "@/components/PolicyLayout";

export default function DataDeletion() {
  return (
    <PolicyLayout
      testId="data-deletion-page"
      seoTitle="Data Retention & Account Deletion — Foodie Crave"
      seoDescription="How Foodie Crave retains account information, how to request account deletion, and what happens afterward."
      seoPath="/data-deletion"
      title="Data Retention & Account Deletion"
      updated="October 20, 2026"
    >
      <PolicySection title="1. Purpose">
        <p>
          Foodie Crave respects users' privacy and their right to request deletion of their account
          and associated personal information. This policy explains how account information is
          maintained, how users may request deletion, and what happens afterward.
        </p>
      </PolicySection>

      <PolicySection title="2. Information We May Retain">
        <ul className="list-disc space-y-2 pl-5">
          <li>Account information — preferred name, email address, settings</li>
          <li>Saved places, visited places, reviews, and related activity</li>
          <li>Friends, friend requests, invitations, and their statuses</li>
          <li>Membership and subscription information</li>
          <li>Customer support inquiries and bug reports</li>
          <li>Security records and technical logs</li>
        </ul>
        <p>
          Information is retained only as long as reasonably necessary to operate the service,
          fulfill business obligations, protect account security, and comply with applicable laws.
        </p>
      </PolicySection>

      <PolicySection title="3. Requesting Account Deletion">
        <p>You can request deletion through:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Within the app — Profile → Delete my account</li>
          <li>Within the app — Profile → Help &amp; Support → Request account deletion</li>
          <li>
            On the web — via the{" "}
            <a
              href="https://forms.zohopublic.com/foodiecrave4ugm1/form/CustomerSupportForm/formperma/RzlQJWKo2FHdmJj54ES7g4Hpv0gkn1cX5RIONNP7omw"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-crave-gold transition-colors duration-300 hover:text-crave-cream"
            >
              Customer Support form
            </a>
          </li>
        </ul>
        <p>You may be required to verify ownership before deletion is processed.</p>
      </PolicySection>

      <PolicySection title="4. What Happens After a Deletion Request">
        <ul className="list-disc space-y-2 pl-5">
          <li>The account is scheduled for permanent deletion</li>
          <li>Personal information and account data are deleted or de-identified, unless retention is required by law</li>
          <li>Account access is terminated once deletion completes</li>
          <li>Information already shared with other users (invitations, votes) may be anonymized while preserving records needed for those users</li>
        </ul>
      </PolicySection>

      <PolicySection title="5. Processing Time">
        <p>
          Deletion requests enter a 7-day grace period during which signing back in reverses the
          request. If no action is taken, personal data is permanently erased from our active
          systems on day 7. Residual copies in encrypted backups and transactional email provider
          logs are rotated out within 30 days of the deletion request.
        </p>
      </PolicySection>

      <PolicySection title="6. Subscription Cancellation">
        <p>
          Deleting your account does not cancel an active subscription managed through Google Play
          or Apple. Cancel through the store's subscription settings to stop future renewals.
        </p>
      </PolicySection>

      <PolicySection title="7. Information That May Be Retained">
        <ul className="list-disc space-y-2 pl-5">
          <li>Legal or regulatory compliance</li>
          <li>Payment, tax, or financial recordkeeping</li>
          <li>Fraud prevention and account security</li>
          <li>Resolving disputes or investigating misuse</li>
          <li>Backup and recovery systems</li>
        </ul>
      </PolicySection>

      <PolicySection title="8. Changes to This Policy">
        <p>
          Foodie Crave may update this policy as services and legal requirements evolve. Material
          changes will be communicated where appropriate.
        </p>
      </PolicySection>

      <PolicySection title="9. Contact">
        <p>
          For questions, use the{" "}
          <a
            href="https://forms.zohopublic.com/foodiecrave4ugm1/form/CustomerSupportForm/formperma/RzlQJWKo2FHdmJj54ES7g4Hpv0gkn1cX5RIONNP7omw"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-crave-gold transition-colors duration-300 hover:text-crave-cream"
          >
            Customer Support form
          </a>{" "}
          or email{" "}
          <a
            href="mailto:support@foodiecrave.app"
            className="font-medium text-crave-gold transition-colors duration-300 hover:text-crave-cream"
          >
            support@foodiecrave.app
          </a>
          .
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
